import google, { fullLongitudeVisible, mutators, removeMap, toLatLng, verify } from "@churchofjesuschrist/maps-google";
import MapsService from "@churchofjesuschrist/maps-service";
import { iconColorDefault, iconSizeDefault, mapExtentProjected } from "@churchofjesuschrist/maps-constants";
import { isFunction, isNotEmpty, isUndefined, noop, proxy, proxyTimeout, removeUndefined, toArray } from "@churchofjesuschrist/maps-utils";
import { getMarkerScale, isPointInRange } from "@churchofjesuschrist/maps-math";
import { isPositionInRange } from "@churchofjesuschrist/maps-google-math";
import Marker, { IconType, LocationType } from "@churchofjesuschrist/maps-google-marker";
import GoogleMapsConverter from "@churchofjesuschrist/maps-google-converter";
let initialized = false;
const defaultOptions = {
  emphasize: false,
  scale: true,
  disableClickZoom: false
};
const selectableSize = {
  width: 2,
  height: 2
};
class Clusters {
  constructor(options = {}) {
    if (!initialized) {
      verify();
      construct();
      initialized = true;
    }
    this.setOptions({
      ...defaultOptions,
      ...removeUndefined(options)
    });
  }
}
const construct = () => {
  GoogleMapsConverter.register();
  Object.setPrototypeOf(Clusters.prototype, Object.assign(new google.maps.OverlayView(), mutators("layers", "ids", "locations", "filters", "emphasize", "scale", "disableClickZoom", "hidePositions", "marker", "iconSize", "map"), (() => {
    function resolveLocations() {
      let value = this.getLocations(),
        locations = null;
      if (isFunction(value)) {
        let results = getClusterParameters.call(this);
        if (results) {
          let {
            extent,
            ...parameters
          } = results;
          locations = value(extent, parameters);
        }
      } else {
        locations = value;
      }
      return locations || [];
    }
    function getClusterParameters() {
      let params = null,
        map = this.getMap(),
        bounds = map.getBounds();
      if (bounds) {
        if (fullLongitudeVisible(map, this)) {
          bounds = new google.maps.LatLngBounds(new google.maps.LatLng(bounds.getSouthWest().lat(), mapExtentProjected.west), new google.maps.LatLng(bounds.getNorthEast().lat(), mapExtentProjected.east));
        }
        params = {
          extent: bounds,
          zoom: map.getZoom(),
          layers: this.getLayers(),
          ids: this.getIds(),
          filters: this.getFilters(),
          icon: calculateIconSize.call(this),
          emphasize: this.getEmphasize()
        };
      }
      return params;
    }
    function calculateIconSize(value = null) {
      let iconSize = this.getIconSize();
      if (iconSize) {
        if (isFunction(iconSize)) {
          iconSize = iconSize(value);
        }
      } else if (this.getScale()) {
        let scale = getMarkerScale(this.getMap().getZoom()),
          size = Math.ceil(iconSizeDefault * scale);
        iconSize = new google.maps.Size(size, size);
      } else {
        iconSize = new google.maps.Size(iconSizeDefault, iconSizeDefault);
      }
      if (iconSize && !(iconSize instanceof google.maps.Size)) {
        iconSize = new google.maps.Size(iconSize.width, iconSize.height);
      }
      return iconSize;
    }
    function calculateIconSizes() {
      let locations = resolveLocations.call(this);
      let count = locations.filter(location => location.position || location.coordinates).length;
      if (!this.$maxCount || count > this.$maxCount) {
        let sizeBuilder = {};
        for (let i = (this.$maxCount || 0) + 1; i <= count; i++) {
          let size = calculateIconSize.call(this, i),
            key = size.toString();
          if (!sizeBuilder[key]) {
            sizeBuilder[key] = {
              size: size,
              counts: []
            };
          }
          sizeBuilder[key].counts.push(i);
        }
        this.$maxCount = count;
        let sizeParams = Object.values(sizeBuilder);
        sizeParams.sort((a, b) => {
          return a.size.width * a.size.height < b.size.width * b.size.height;
        });
        this.$sizeParams = sizeParams;
      }
      return this.$sizeParams;
    }
    function processLocationsClusters() {
      if (this.getMap() && this.getLocations() && this.getProjection()) {
        let clusters;
        let iconSize = this.getIconSize();
        if (!isFunction(iconSize)) {
          clusters = calculateLocationsClusters.call(this, iconSize);
        } else {
          let iconSizes = calculateIconSizes.call(this),
            clustered = [],
            points = [];
          clusters = iconSizes.map(params => {
            return calculateLocationsClusters.call(this, params.size, params.counts, clustered, points);
          }).flat();
        }
        drawClusters.call(this, clusters);
      }
    }
    function calculateLocationsClusters(iconSize, minCounts = null, clustered = [], points = []) {
      let clusters = [];
      let locations = resolveLocations.call(this),
        emphasize = this.getEmphasize(),
        filters = this.getFilters();
      if (!iconSize) {
        iconSize = calculateIconSize.call(this);
      }
      locations.forEach((location, index) => {
        if (location.coordinates && !clustered[index]) {
          let include = filtersMatch(filters, location);
          if (include || emphasize) {
            let cluster = {
              coordinates: toLatLng(location.coordinates),
              locations: [location],
              dispersed: false,
              emphasized: include && emphasize
            };
            let clusterPoint = getLocationPoint.call(this, index, points, locations),
              clusterIndicies = [];
            for (let i = index + 1; i < locations.length; i++) {
              let currentLocation = locations[i],
                currentPoint = getLocationPoint.call(this, i, points, locations);
              if (currentPoint && !clustered[i] && isPointInRange(clusterPoint, currentPoint, iconSize)) {
                clusterIndicies.push(i);
                let currentInclude = filtersMatch(filters, currentLocation);
                if (currentInclude || emphasize) {
                  cluster.locations.push(currentLocation);
                  if (!cluster.dispersed && !cluster.coordinates.equals(toLatLng(currentLocation.coordinates))) {
                    cluster.dispersed = true;
                  }
                  if (currentInclude && emphasize) {
                    cluster.emphasized = true;
                  }
                }
              }
            }
            if (!minCounts || minCounts.indexOf(cluster.locations.length) !== -1) {
              clusters.push(cluster);
              clusterIndicies.push(index);
              clusterIndicies.forEach(clusterIndex => {
                clustered[clusterIndex] = true;
              });
            }
          }
        }
      });
      return clusters;
    }
    function getLocationPoint(index, points, locations) {
      if (isUndefined(points[index])) {
        let location = locations[index];
        if (location.coordinates) {
          points[index] = this.getProjection().fromLatLngToContainerPixel(toLatLng(location.coordinates));
        } else {
          points[index] = null;
        }
      }
      return points[index];
    }
    function filtersMatch(filters, location) {
      let matches = true;
      if (filters) {
        matches = false;
        if (isFunction(filters)) {
          matches = filters(location);
        } else {
          //TODO: use standard filters against standard location object
        }
      }
      return matches;
    }
    function drawClusters(clusters) {
      let markers = clusters.map(proxy(generateMarkers, this)).flat().filter(isNotEmpty);
      this.clear();
      this.$markers = markers;
      clearTimeout(this.$idleTimer);
      this.$idleTimer = proxyTimeout(() => {
        google.maps.event.trigger(this, "idle");
      }, this, 100);
    }
    function generateMarkers(cluster) {
      let map = this.getMap(),
        position = cluster.coordinates,
        visible = showCluster.call(this, cluster),
        markerOptions = {
          position,
          visible
        };
      let markerSpec = this.getMarker();
      if (markerSpec && !isFunction(markerSpec)) {
        Object.assign(markerOptions, markerSpec);
      } else if (cluster.counts) {
        let layer = cluster.counts[0].type,
          invert = this.getFilters() && this.getEmphasize() ? !cluster.emphasized : false,
          scale = this.getScale() && map ? getMarkerScale(map.getZoom(), invert ? 0.82 : 1) : null,
          //TODO: verify invert const
          symbol = scale && scale < 0.4 ? "blank" : null,
          icon = {
            layer,
            invert,
            scale,
            symbol
          },
          zIndex = invert ? 1 : 2;
        Object.assign(markerOptions, {
          icon,
          zIndex
        });
      } else {
        Object.assign(markerOptions, {
          icon: {
            color: iconColorDefault
          }
        });
      }
      let markers;
      if (markerSpec && isFunction(markerSpec)) {
        let results = markerSpec(cluster, markerOptions);
        markers = toArray(results).map(result => {
          let marker;
          if (result instanceof Marker || result instanceof google.maps.Marker) {
            marker = result;
            if (!marker.getPosition()) {
              marker.setPosition(position);
              marker.setVisible(visible);
            }
          } else {
            marker = new Marker({
              position,
              visible,
              ...result
            });
          }
          return marker;
        });
      } else {
        markers = [new Marker(markerOptions)];
      }
      markers.forEach(marker => {
        marker.addListener("click", event => {
          markerClick.call(this, marker, cluster, event);
        });
        marker.setMap(map);
      });
      return markers;
    }
    function markerClick(marker, cluster, event) {
      let newEvent = {
        ...event,
        marker,
        cluster
      };
      let zoomed = false;
      if (isZoomable.call(this, cluster)) {
        let bounds = cluster.extent;
        if (!bounds && cluster.locations) {
          let positions = cluster.locations.map(location => location.coordinates).filter(coordinates => !!coordinates);
          if (positions.length) {
            bounds = new google.maps.LatLngBounds();
            positions.forEach(position => {
              bounds.extend(toLatLng(position));
            });
            cluster.extent = bounds;
          }
        }
        if (bounds) {
          this.getMap().fitBounds(bounds);
          zoomed = true;
        }
        google.maps.event.trigger(this, "click_zoom", newEvent);
      }
      if (!zoomed) {
        google.maps.event.trigger(this, "click", newEvent);
      }
    }
    function showCluster(cluster) {
      let positions = this.getHidePositions();
      if (positions?.length) {
        return positions.every(position => !isPositionInRange(cluster.coordinates, position, selectableSize, this));
      }
      return true;
    }
    function isZoomable(cluster) {
      let zoomable = false;
      if (!this.getDisableClickZoom() && cluster.dispersed) {
        let map = this.getMap(),
          type = map.mapTypes[map.getMapTypeId()];
        if (type) {
          zoomable = type.maxZoom > map.getZoom();
        }
      }
      return zoomable;
    }
    return {
      refresh: function () {
        clearTimeout(this.$idleTimer);
        let map = this.getMap();
        if (map && (this.getLayers() || this.getIds())) {
          clearTimeout(this.$targetRefreshTimer);
          let currentTimer = this.$targetRefreshTimer = proxyTimeout(() => {
            let results = getClusterParameters.call(this);
            if (results) {
              let {
                extent,
                ...parameters
              } = results;
              MapsService.getLocationsClusters(extent, parameters).then(clusters => {
                if (currentTimer === this.$targetRefreshTimer) {
                  drawClusters.call(this, clusters);
                }
              });
            }
          }, this);
        } else if (map && this.getLocations() && this.getProjection()) {
          this.$targetRefreshTimer = proxyTimeout(processLocationsClusters, this);
        } else {
          this.clear();
        }
      },
      clear: function () {
        let markers = this.$markers;
        this.$markers = [];
        if (markers) {
          setTimeout(() => removeMap(markers), 100);
        }
      },
      identify: function (cluster) {
        return new Promise(resolve => {
          if (cluster.locations?.length) {
            resolve(cluster.locations);
          } else if (this.getLayers() || this.getIds()) {
            let parameters = getClusterParameters.call(this);
            if (parameters) {
              MapsService.identifyLocationClusters(cluster.coordinates, parameters).then(resolve);
            }
          }
        });
      },
      setHidePosition: function (position) {
        this.setHidePositions(position ? [position] : null);
      },
      getHidePosition: function () {
        let positions = this.getHidePositions();
        return positions?.length ? positions[0] : null;
      },
      onAdd: function () {
        if (!this.$listener) {
          let map = this.getMap();
          this.$listener = map.addListener("idle", proxy(this.refresh, this));
        }
        if (this.getProjection()) {
          proxyTimeout(this.refresh, this);
        }
      },
      onRemove: function () {
        if (this.$listener) {
          this.$listener.remove();
          delete this.$listener;
        }
        this.clear();
      },
      draw: noop,
      changed: function (key) {
        switch (key) {
          case "layers":
          case "ids":
          case "filters":
          case "emphasize":
          case "scale":
          case "marker":
          case "hidePositions":
            this.refresh();
            break;
          case "locations":
          case "iconSize":
            delete this.$maxCount;
            delete this.$sizeParams;
            this.refresh();
            break;
        }
      }
    };
  })()));
};
export default Clusters;
export { IconType, LocationType };